package app.smartcoding.realrate.domain.usecase

import app.smartcoding.realrate.domain.model.AmortizationRow
import kotlinx.coroutines.yield
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus
import kotlin.math.abs
import kotlin.math.pow

class AnalyzeCreditUseCase {

    fun pmt(principal: Double, annualRatePercent: Double, months: Int): Double {
        if (annualRatePercent == 0.0) return principal / months
        val r = annualRatePercent / 12 / 100
        return (principal * r) / (1 - (1 + r).pow(-months))
    }

    suspend fun calculateMonthlyRate(months: Int, monthlyPayment: Double, principal: Double): Double? {
        if (months <= 0 || monthlyPayment <= 0 || principal <= 0) return null
        if (monthlyPayment * months <= principal) return 0.0

        var lowRate = 0.0
        var highRate = 100.0
        val precision = 1e-7
        val maxIterations = 100

        repeat(maxIterations) {
            yield()
            val midRate = (lowRate + highRate) / 2
            if (midRate == 0.0) return 0.0

            val calculatedPv = monthlyPayment * (1 - (1 + midRate).pow(-months)) / midRate

            if (abs(calculatedPv - principal) < precision) {
                return midRate
            }

            if (calculatedPv > principal) {
                lowRate = midRate
            } else {
                highRate = midRate
            }
        }

        val finalRate = (lowRate + highRate) / 2
        return if (abs(finalRate) < precision) 0.0 else finalRate
    }

    fun calculateNominalRate(monthlyRate: Double): Double {
        return monthlyRate * 12 * 100
    }

    fun calculateEffectiveApr(monthlyRate: Double): Double {
        if (monthlyRate == 0.0) return 0.0
        return ((1 + monthlyRate).pow(12) - 1) * 100
    }

    suspend fun generateSchedule(
        principal: Double,
        monthlyRate: Double, // Подаваме вече сметнатата лихва!
        months: Int,
        monthlyPayment: Double,
        startDate: LocalDate
    ): List<AmortizationRow> {
        val schedule = mutableListOf<AmortizationRow>()
        var balance = principal

        // Тук вече не делим на 12 и 100, защото подаваме готовата месечна лихва (decimal)
        // Внимавай: Ако подаваш Nominal Rate тук, трябва да го конвертираш.
        // Но за по-бързо, по-добре подай директно monthlyRate от стъпка 1.

        for (month in 1..months) {
            if (balance <= 0.001) break
            yield() // Важно за прекъсването!

            val interest = balance * monthlyRate
            var principalPayment = monthlyPayment - interest

            if (principalPayment > balance) {
                principalPayment = balance
            }

            balance -= principalPayment
            val finalBalance = if (balance < 0.01) 0.0 else balance
            val paymentDate = startDate.plus(month - 1, DateTimeUnit.MONTH)

            schedule.add(
                AmortizationRow(
                    month = month,
                    date = paymentDate,
                    payment = principalPayment + interest,
                    interest = interest,
                    principal = principalPayment,
                    balance = finalBalance
                )
            )
        }
        return schedule
    }

    private suspend fun getMonthlyRate(months: Int, monthlyPayment: Double, principal: Double): Double? {
        // Basic validation for inputs
        if (months <= 0 || monthlyPayment <= 0 || principal <= 0) {
            return null
        }
        // If total payments are less than or equal to the loan amount, the rate is effectively zero or negative.
        if (monthlyPayment * months <= principal) {
            return 0.0
        }

        var lowRate = 0.0
        var highRate = 100.0 // Increased upper bound significantly to cover extreme cases

        val precision = 1e-7
        val maxIterations = 100

        repeat(maxIterations) {
            yield()
            val midRate = (lowRate + highRate) / 2
            if (midRate == 0.0) return 0.0 // Avoid division by zero if we hit the bottom boundary

            val calculatedPv = monthlyPayment * (1 - (1 + midRate).pow(-months)) / midRate

            if (abs(calculatedPv - principal) < precision) {
                return midRate
            }

            if (calculatedPv > principal) {
                // Calculated PV is too high, meaning the rate is too low
                lowRate = midRate
            } else {
                // Calculated PV is too low, meaning the rate is too high
                highRate = midRate
            }
        }

        // Return the best guess if max iterations are reached
        val finalRate = (lowRate + highRate) / 2
        // If the result is extremely close to zero, treat it as zero.
        return if (abs(finalRate) < precision) 0.0 else finalRate
    }

    /**
     * Calculates the effective annual rate (EAR), which is the APR.
     * Formula: (1 + monthly_rate)^12 - 1
     * Returns null if the rate cannot be calculated.
     */
    suspend fun effectiveApr(months: Int, monthlyPayment: Double, principal: Double): Double? {
        val monthly = getMonthlyRate(months, monthlyPayment, principal) ?: return null
        // If monthly rate is 0, APR is 0
        if (monthly == 0.0) return 0.0
        return ((1 + monthly).pow(12) - 1) * 100
    }
}